import type { ReactNode } from 'react'
import { Tabs } from '@heroui/react'

/**
 * The chrome every Back Office module wears.
 *
 * Staff arrived at this shape first and the rest of the modules now follow it:
 *
 *  - The page carries no title of its own. The app bar names the screen and
 *    holds the way back — see `page-identity.ts`. A module that painted its own
 *    heading would put two titles a row apart.
 *  - The body is full-bleed on a grey canvas, with a white bar under the app
 *    bar. That inverts the shell's flat sheet, which is what makes the tables
 *    below read as cards rather than as regions of the page.
 *  - The bar holds the module's sections on the left and the filters for the
 *    visible section on the right. Filters belong beside the tabs, not inside
 *    the panel: they change what the panel shows rather than being part of it.
 *  - Everything below lines up on the same centred column as the bar's contents.
 */

/** Every panel's gutters. Shared so the tab row and the body stay in step. */
export const MODULE_PANEL = 'mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8'

export interface ModuleTab {
  id: string
  label: ReactNode
}

interface ModuleShellProps {
  /**
   * The sections this module splits into. Omit for a module with one section —
   * the bar then carries only its filters, and `children` is the body.
   */
  tabs?: ModuleTab[]
  selectedKey?: string
  onSelectionChange?: (key: string) => void
  /**
   * The module's own navigation, where the tabs would sit, for a module whose
   * sections are urls rather than panels — Reports. Ignored when `tabs` is set.
   */
  nav?: ReactNode
  /**
   * Filters for the visible section, on the right of the bar. Computed by the
   * caller from the current tab, since each section filters by its own things.
   */
  toolbar?: ReactNode
  /**
   * Dialogs that outlive the visible section.
   *
   * They render outside the tab machinery: a modal is not one of the tabs'
   * panels, and React Aria's Tabs owns what sits between its list and its
   * panels. A dialog that belongs to one section can just live in that panel
   * instead.
   */
  overlays?: ReactNode
  children: ReactNode
}

export function ModuleShell({
  tabs,
  selectedKey,
  onSelectionChange,
  nav,
  toolbar,
  overlays,
  children,
}: ModuleShellProps) {
  /* The min-height matches the app bar's two heights so a short module still
     paints its canvas to the bottom of the window rather than stopping at the
     end of its content. */
  const canvas =
    'bg-background flex min-h-[calc(100dvh-4rem)] flex-col sm:min-h-[calc(100dvh-72px)]'

  if (!tabs?.length && nav) {
    return (
      <div className={canvas}>
        <ModuleBar>
          {nav}
          {toolbar && <span className="pb-2">{toolbar}</span>}
        </ModuleBar>

        <div className={MODULE_PANEL}>{children}</div>

        {overlays}
      </div>
    )
  }

  if (!tabs?.length) {
    return (
      <div className={canvas}>
        {toolbar && (
          /* Collapsed when the toolbar renders to nothing, so a module whose
             only filter comes out empty does not paint a bar with a rule under
             it and nothing in it. The page cannot hide it itself — it holds an
             element, not the knowledge of whether that element comes out empty
             — so the bar reads its own content.

             A safety net rather than a load-bearing rule now: the branch pickers
             used to draw nothing for a single-outlet business, which is what
             this was written for, and they no longer do. Kept because the next
             conditional toolbar will not announce itself either. */
          <ModuleBar className="[&:has(>div>span:empty)]:hidden">
            {/* Alone on the row, so it pushes itself to the end rather than
                sitting where the tabs would have been. */}
            <span className="ms-auto pb-2">{toolbar}</span>
          </ModuleBar>
        )}

        <div className={MODULE_PANEL}>{children}</div>

        {overlays}
      </div>
    )
  }

  return (
    <div className={canvas}>
      <Tabs selectedKey={selectedKey} onSelectionChange={(key) => onSelectionChange?.(String(key))}>
        <ModuleBar>
          {/* The theme sets `.tabs__list[data-orientation=horizontal]` to
              `min-width: 100%`, and an attribute selector outranks a plain
              utility — hence the important modifier. Without it the list eats
              the whole row and pushes the filters onto a line of their own.
              `w-auto` on each tab undoes the matching `width: 100%`. */}
          <Tabs.List className="min-w-0! w-fit shrink-0">
            {tabs.map((tab) => (
              <Tabs.Tab key={tab.id} id={tab.id} className="w-auto">
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {/* Lifted off the rule by the same amount the row is padded above, so
              the control sits in the middle of the space it has. */}
          {toolbar && <span className="pb-2">{toolbar}</span>}
        </ModuleBar>

        {children}
      </Tabs>

      {overlays}
    </div>
  )
}

/** One section's body. Only meaningful inside a `ModuleShell` that has tabs. */
export function ModulePanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Tabs.Panel id={id} className={MODULE_PANEL}>
      <div className="flex flex-col gap-4">{children}</div>
    </Tabs.Panel>
  )
}

/**
 * The line of copy at the top of a panel, with the section's primary action
 * beside it.
 *
 * The action lives here rather than in the bar above: the bar answers "which
 * section am I looking at and how is it filtered", and a button that creates
 * something belongs with the thing it creates.
 */
export function ModuleIntro({
  description,
  action,
}: {
  description: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <p className="text-muted max-w-xl text-sm">{description}</p>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  )
}

/**
 * The white strip under the app bar.
 *
 * The rule runs the full width while its contents line up with the column
 * below. `items-end` keeps the active tab's underline sitting on that rule —
 * centring the row would float it mid-air — while the top padding gives a
 * filter beside it room to breathe instead of wedging a 36px control into a
 * 44px row.
 */
function ModuleBar({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-surface border-border border-b ${className}`}>
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-end justify-between gap-x-3 gap-y-2 px-4 pt-2 sm:px-6">
        {children}
      </div>
    </div>
  )
}
